// client/src/components/Courses.js
// Courses management component for superadmins

import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { HeadlineLarge, BodyMedium } from './Typography';

const AdminCourses = ({ onNavigateToCourse }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load all courses
  useEffect(() => {
    const loadCourses = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const coursesRef = collection(db, 'courses');
        const coursesSnapshot = await getDocs(coursesRef);
        
        const coursesData = [];
        coursesSnapshot.forEach(doc => {
          coursesData.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        // Sort by course name
        coursesData.sort((a, b) => (a.course_name || '').localeCompare(b.course_name || ''));
        setCourses(coursesData);
      } catch (error) {
        console.error('Error loading courses:', error);
        setError('Failed to load courses. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadCourses();
  }, []);

  const handleAddCourse = () => {
    // Navigate to blank course edit page for new course
    onNavigateToCourse(null);
  };

  const handleEditCourse = (courseId) => {
    // Navigate to course edit page for existing course
    onNavigateToCourse(courseId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading courses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <HeadlineLarge color="gray-900">Courses</HeadlineLarge>
        <button
          onClick={handleAddCourse}
          className="px-4 py-2 bg-blue-100 text-blue-700 border border-blue-300 rounded hover:bg-blue-200 transition-colors"
        >
          Add Course
        </button>
      </div>

      {/* Courses Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Course Name
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Course Headline
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Hours to Complete
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Lessons Count
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {courses.map((course) => (
              <tr key={course.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <BodyMedium color="gray-900">{course.course_name || 'Unnamed Course'}</BodyMedium>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <BodyMedium color="gray-600">{course.course_headline || '-'}</BodyMedium>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <BodyMedium color="gray-600">
                    {course.hours_to_complete || '-'}
                  </BodyMedium>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <BodyMedium color="gray-600">
                    {course.lessons_count || '-'}
                  </BodyMedium>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <button
                    onClick={() => handleEditCourse(course.id)}
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Results Count */}
      <div className="text-sm text-gray-500 text-center">
        Showing {courses.length} course{courses.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
};

export default AdminCourses;
